import React from "react";
import {IconButton, Link, Typography} from "@material-ui/core";
import {
    ActionDto,
    ManagerDto,
    ReviewEmployeeAccountDto,
    ReviewEmployeeDto, RolePermissionDto,
    RolePermissionInfo
} from "src/stores/models/qarFormModels";
import { EmployeeReviewActions } from "src/stores/models/enums";
import { NamedPersonDto } from "src/stores/models/namedPerson";
import {HtmlTooltip} from "../../components/HtmlTooltip/HtmlTooltip";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import MaterialTable, {Column} from "material-table";
import {chameleonSystem} from "../../helper/constants";
import {RootStoreSingleton} from "../../stores/rootStore";


class QARFormHelper {

    static renderTooltipMessage(tooltipMessage: string) {
        return <Typography component="h2" align='center'>{tooltipMessage}</Typography>;
    }

    static employeeDelegatedFrom(employee: ReviewEmployeeDto) : NamedPersonDto | undefined {
        return employee.accounts.find(x => x.delegatedFromManager)?.delegatedFromManager;
    }

    static employeeDelegatedTo(employee: ReviewEmployeeDto) : ManagerDto | undefined {
        return QARFormHelper.accountDelegatedTo(employee.accounts);
    }

    static accountDelegatedTo(accounts: ReviewEmployeeAccountDto[]) : ManagerDto | undefined {
        return accounts.find(x => x.action.delegatedTo !== null)?.action.delegatedTo as ManagerDto;
    }

    static isEmployeeReviewCompleted = (employee: ReviewEmployeeDto) => {
        return employee.accounts.every(x => x.action.name !== EmployeeReviewActions.MissingAction);
    }
        

    static accountBulkReviewDisabled(action: ActionDto, actionChanges : ActionDto) : boolean {

        // the initial state of this account was approved
        // and the change of the action does not differ
        if(action.name === EmployeeReviewActions.Approved && actionChanges.name ===  EmployeeReviewActions.Approved) {
            return true;
        }

        if(actionChanges.name === EmployeeReviewActions.Revoked) {
            return true;
        }
    
        if(actionChanges.name === EmployeeReviewActions.Change) {
            return true;
        }

        return false;
    }

    static masterBulkReviewDisabled(accounts : ReviewEmployeeAccountDto[]) : boolean {
        // NOTE: Signifies that all accounts have a saved action - no one left to approve
        return accounts.every(x => x.action.name !== EmployeeReviewActions.MissingAction);
    }

    static masterBulkReviewChecked(accounts : ReviewEmployeeAccountDto[], accountChanges : ReviewEmployeeAccountDto[]) : boolean {
        if(QARFormHelper.masterBulkReviewDisabled(accounts)) {
            return false;
        }
        
        for(let i = 0; i < accounts.length; i++) {
            if(QARFormHelper.accountBulkReviewDisabled(accounts[i].action, accountChanges[i].action)) {
                continue;
            }

            if(accountChanges[i].action.name !== EmployeeReviewActions.Approved) {
                return false;
            }
        }

        return true;
    }

    static renderPermissionGroups = (employeeAccount: ReviewEmployeeAccountDto) => {
        return (
            <span className="reportGroups">
                <Link href="#"
                      onClick={(event: React.MouseEvent) => QARFormHelper.handleUsernameClick(event, employeeAccount)}>{employeeAccount.username}</Link>
                            <HtmlTooltip
                                title={
                                    <><Typography color="inherit" variant="caption">Click on username to see its
                                        permission groups.</Typography></>
                                }
                            >
                                <IconButton aria-label="delete" size="small">
                                    <HelpOutlineIcon fontSize="inherit"/>
                                </IconButton>
                            </HtmlTooltip>
                        </span>
        )
    }

    static renderRole = (roles: any) => {
        // here we can customize rendering of cells based on their field name

        if (!roles) {
            return;
        }

        if (roles.length === 0) {
            return (
                <p>This account has no associated role!</p>
            );
        }

        return (
            <span className="reportRole">
                    {/*
                    NOTE: It is an extreme case that a user has two or more roles
                    but the following code ensures that we handle the case regardless
                */}
                {roles
                    .map((role: any) => {
                        return (
                            <Link href="#" onClick={(event: React.MouseEvent) => QARFormHelper.handleRoleClick(event, role)}>
                                {role.name}
                            </Link>
                        );
                    })
                    .reduce((previous: typeof Link, current: typeof Link) => [
                        previous,
                        ", ",
                        current,
                    ])}
                <HtmlTooltip
                    title={
                        <React.Fragment>
                            <Typography color="inherit" variant="caption">
                                Click on role name to see its groups.
                            </Typography>
                        </React.Fragment>
                    }
                >
                        <IconButton aria-label="delete" size="small">
                            <HelpOutlineIcon fontSize="inherit" />
                        </IconButton>
                    </HtmlTooltip>
            </span>
        );
    };

    static renderRolePermissionGroupsTable = (roleName: string, rolePermissionInfo: RolePermissionInfo | undefined) => {

        if (rolePermissionInfo === undefined) {
            return;
        }

        const columns: Column<RolePermissionDto>[] = [
            { title: 'Name', field: 'name', defaultSort: "asc" },
            { title: 'Description', field: 'description' },
        ]

        if (rolePermissionInfo.systemName === chameleonSystem) {
            columns.push({ title: 'Permission Actions', field: 'permissionActions', render: (rowdata: RolePermissionDto) => <>{rowdata.permissionActions?.join(', ')}</> })
        }

        return (
            <>
                <h2>Role '{roleName}' has a total of {rolePermissionInfo.rolePermissions.length} permission {rolePermissionInfo.rolePermissions.length !== 1 ? "groups" : "group"}</h2>
                <MaterialTable
                    options={{
                        grouping: false,
                        draggable: false,
                        showTitle: false,
                        sorting: true,
                        pageSize: 15,
                        filtering: true,
                        paging: true,
                        search: true,
                        toolbar: true,
                        pageSizeOptions: [5, 10, 15]
                    }}
                    data={rolePermissionInfo.rolePermissions}
                    columns={columns}
                />
            </>
        );
    }

    static renderUserPermissionGroupsTable = (employeeAccount: ReviewEmployeeAccountDto) => {
        return (
            <>
                <h2>
                    User '{employeeAccount.username}' has a total
                    of {employeeAccount.permissionGroups.length} permission {employeeAccount.permissionGroups.length !== 1 ? "groups" : "group"} in {employeeAccount.displayName} ({employeeAccount.systemName})
                </h2>

                <MaterialTable
                    options={{
                        grouping: false,
                        draggable: false,
                        showTitle: false,
                        sorting: true,
                        pageSize: 15,
                        filtering: true,
                        paging: true,
                        search: true,
                        toolbar: true,
                        pageSizeOptions: [5, 10, 15]
                    }}
                    data={employeeAccount.permissionGroups}
                    columns={[
                        {title: 'Name', field: 'name', defaultSort: "asc"},
                        {title: 'Description', field: 'description'}
                    ]}
                />
            </>
        );
    }

    static handleUsernameClick = (event: React.MouseEvent, employeeAccount: ReviewEmployeeAccountDto) => {
        const uiStateStore = RootStoreSingleton.uiStateStore;
        event.preventDefault(); // this prevents appending hashtag to the url

        uiStateStore.setModalContent(QARFormHelper.renderUserPermissionGroupsTable(employeeAccount))
        uiStateStore.setModalOpen(true);
    }

    static handleRoleClick = async (event: React.MouseEvent, role: any) => {
        const uiStateStore = RootStoreSingleton.uiStateStore;
        const qarFormStore = RootStoreSingleton.qarFormStore;
        event.preventDefault();

        await qarFormStore.loadRolePermissionInfo(role.id);

        uiStateStore.setModalContent(QARFormHelper.renderRolePermissionGroupsTable(role.name, qarFormStore.rolePermissionInfo))
        uiStateStore.setModalOpen(true);
    }
}

export default QARFormHelper;